/*
 * bb-cart-morph.js — keyed in-place DOM patcher for the cart drawer, plus the
 * optimistic free-shipping-bar helpers.
 *
 * Replaces the old innerHTML region swaps (scroll reset, focus loss, ship-bar
 * rebuilt every update so its width transition never fired): line rows match
 * by data-line-key (snippets/cart-drawer.liquid), the rest pairs by tag +
 * first class; attrs/text update only on real change so role="status" regions
 * announce once. Every caller feature-detects window.BBCartMorph and keeps a
 * legacy innerHTML path; morph() itself falls back to an innerHTML swap
 * (returns false) when its post-patch sanity check disagrees with the source.
 * Classic script, silent in production.
 */
(function () {
  'use strict';
  if (window.BBCartMorph) return;

  /* Transient client-side classes the server never renders — never strip. */
  var KEEP_CLASSES = {
    'bb-line-enter': 1,
    'bb-line-exit': 1,
    'bb-ship-bar--just-met': 1,
    'bb-cart-ship-bar--just-met': 1
  };
  /* Runtime a11y wiring (e.g. the note summary's role) — keep when the server
     markup simply lacks it; still overwritten when the server sets it. */
  var KEEP_ATTRS_IF_ABSENT = { role: 1, 'aria-expanded': 1, 'aria-controls': 1 };

  function norm(s) {
    return (s || '').replace(/\s+/g, ' ').trim();
  }

  /* Rows mid-exit are client-side ghosts the server no longer knows about. */
  function significant(n) {
    if (n.nodeType === 1) return !(n.classList && n.classList.contains('bb-line-exit'));
    return n.nodeType === 3 && norm(n.nodeValue) !== '';
  }

  function keyOf(n) {
    return n.nodeType === 1 && n.getAttribute ? n.getAttribute('data-line-key') : null;
  }

  function sig(n) {
    if (n.nodeType !== 1) return '#text';
    var c = n.getAttribute('class');
    return n.nodeName + '.' + (c ? norm(c).split(' ')[0] : '');
  }

  function syncAttrs(t, s) {
    var i, name;
    for (i = t.attributes.length - 1; i >= 0; i--) {
      name = t.attributes[i].name;
      if (name === 'class') continue;
      if (name === 'open' && t.nodeName === 'DETAILS') continue; /* user-toggled */
      if (KEEP_ATTRS_IF_ABSENT[name]) continue;
      if (!s.hasAttribute(name)) t.removeAttribute(name);
    }
    for (i = 0; i < s.attributes.length; i++) {
      name = s.attributes[i].name;
      if (name === 'class') continue;
      if (t.getAttribute(name) !== s.attributes[i].value) t.setAttribute(name, s.attributes[i].value);
    }
    var want = s.getAttribute('class') || '';
    var tc = (t.getAttribute('class') || '').split(/\s+/);
    for (i = 0; i < tc.length; i++) {
      if (KEEP_CLASSES[tc[i]] && (' ' + want + ' ').indexOf(' ' + tc[i] + ' ') === -1) want += ' ' + tc[i];
    }
    want = norm(want);
    if ((t.getAttribute('class') || '') !== want) {
      if (want) t.setAttribute('class', want);
      else t.removeAttribute('class');
    }
  }

  function patchEl(t, s) {
    if (t.nodeName === 'INPUT' || t.nodeName === 'TEXTAREA' || t.nodeName === 'SELECT') {
      /* Never fight the user's caret. The value ATTRIBUTE is still synced (it
         carries server truth for the optimistic math), but on a clean input
         an attribute write also moves the live value — so capture/restore the
         property when the field is focused. */
      if (document.activeElement === t) {
        var live = t.value;
        syncAttrs(t, s);
        if (t.value !== live) t.value = live;
      } else {
        syncAttrs(t, s);
        if (t.value !== s.value) t.value = s.value;
      }
      return;
    }
    syncAttrs(t, s);
    walk(t, s);
  }

  function enter(el) {
    el.classList.add('bb-line-enter');
    var done = function () { el.classList.remove('bb-line-enter'); };
    el.addEventListener('animationend', done, { once: true });
    setTimeout(done, 600);
  }

  /* Collapse-and-remove a line row: measured px height -> class transitions to
     0; removed on transitionend with a timeout fallback. Idempotent. */
  function exitRow(el) {
    if (!el || el.nodeType !== 1 || el.classList.contains('bb-line-exit')) return;
    el.style.height = el.offsetHeight + 'px';
    void el.offsetHeight;
    el.classList.add('bb-line-exit');
    var drop = function () {
      if (!el.classList.contains('bb-line-exit')) return; /* revived */
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    el.addEventListener('transitionend', function (e) {
      if (e.target === el && e.propertyName === 'height') drop();
    });
    el.__bbExitT = setTimeout(drop, 400);
  }

  /* The exiting row still exists server-side (soft error / rollback). */
  function reviveRow(el) {
    if (!el) return;
    el.classList.remove('bb-line-exit');
    el.style.height = '';
    if (el.__bbExitT) { clearTimeout(el.__bbExitT); el.__bbExitT = null; }
  }

  function walk(t, s) {
    /* Equal-markup shortcut — but never over form fields: a control's live
       value is a PROPERTY (invisible to innerHTML) and may still need
       reconciling to the server value carried by its attribute. */
    if (t.innerHTML === s.innerHTML && !t.querySelector('input,textarea,select')) return;

    var tKids = [], sKids = [], n, i, j, k;
    for (n = t.firstChild; n; n = n.nextSibling) { if (significant(n)) tKids.push(n); }
    for (n = s.firstChild; n; n = n.nextSibling) { if (significant(n)) sKids.push(n); }

    var byKey = {};
    for (i = 0; i < tKids.length; i++) {
      k = keyOf(tKids[i]);
      if (k) byKey[k] = tKids[i];
    }

    var used = [], out = [];
    for (i = 0; i < sKids.length; i++) {
      var sk = sKids[i];
      var match = null;
      k = keyOf(sk);
      if (k) {
        match = byKey[k] || null;
        if (!match) {
          /* an optimistically-exiting twin? revive instead of duplicating */
          for (n = t.firstChild; n; n = n.nextSibling) {
            if (n.nodeType === 1 && keyOf(n) === k && n.classList.contains('bb-line-exit')) {
              reviveRow(n);
              match = n;
              break;
            }
          }
        }
      } else {
        for (j = 0; j < tKids.length; j++) {
          if (used.indexOf(tKids[j]) === -1 && !keyOf(tKids[j]) && sig(tKids[j]) === sig(sk)) {
            match = tKids[j];
            break;
          }
        }
      }
      if (match) {
        used.push(match);
        if (match.nodeType === 3) {
          if (norm(match.nodeValue) !== norm(sk.nodeValue)) match.nodeValue = sk.nodeValue;
        } else {
          patchEl(match, sk);
        }
        out.push(match);
      } else {
        var fresh = document.importNode(sk, true);
        if (k) fresh.__bbEnter = true;
        out.push(fresh);
      }
    }

    /* Reorder/insert back-to-front; ghosts and whitespace stay put. */
    var ref = null;
    for (i = out.length - 1; i >= 0; i--) {
      n = out[i];
      if (n.parentNode === t) {
        var nx = n.nextSibling;
        while (nx && nx !== ref && out.indexOf(nx) === -1) nx = nx.nextSibling;
        if (nx === ref) { ref = n; continue; }
      }
      t.insertBefore(n, ref);
      if (n.__bbEnter) { n.__bbEnter = false; enter(n); }
      ref = n;
    }

    for (i = 0; i < tKids.length; i++) {
      n = tKids[i];
      if (used.indexOf(n) !== -1) continue;
      if (n.nodeType === 1 && keyOf(n)) exitRow(n);
      else if (n.parentNode === t) t.removeChild(n);
    }
  }

  /* Patch live `target` to match parsed `source`. True = keyed patch held;
     false = fell back to an innerHTML swap (callers re-bind region listeners). */
  function morph(target, source) {
    if (!target || !source) return false;
    try {
      patchEl(target, source);
      var tRows = target.querySelectorAll('.cart-item:not(.bb-line-exit)').length;
      if (tRows !== source.querySelectorAll('.cart-item').length) throw new Error('rows');
      var sTot = source.querySelector('.totals__total-value');
      var tTot = target.querySelector('.totals__total-value');
      if (sTot && (!tTot || norm(tTot.textContent) !== norm(sTot.textContent))) throw new Error('totals');
      return true;
    } catch (e) {
      try { target.innerHTML = source.innerHTML; } catch (e2) { /* leave as-is */ }
      return false;
    }
  }

  /* Format cents re-using the separators/symbol of a sample money string. */
  function formatMoneyLike(sample, cents) {
    sample = String(sample);
    var m = sample.match(/\d[\d.,\s ']*\d|\d/);
    if (!m) return sample;
    var num = m[0];
    var decChar = '';
    var c = num.charAt(num.length - 3);
    if (c === '.' || c === ',') decChar = c;
    var thouChar = '';
    for (var i = 0; i < num.length; i++) {
      var ch = num.charAt(i);
      if ('0123456789'.indexOf(ch) === -1 && !(ch === decChar && i === num.length - 3)) {
        thouChar = ch;
        break;
      }
    }
    var abs = Math.round(Math.max(0, cents));
    var intPart = String(Math.floor(abs / 100));
    var frac = String(abs % 100);
    if (frac.length < 2) frac = '0' + frac;
    if (thouChar) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thouChar);
    // Drop the decimals on a whole amount ("64 CAD") but keep them otherwise
    // ("26.50 CAD") — mirrors snippets/bb-money so optimistic and server prices match.
    var body = intPart;
    if (decChar && frac !== '00') body = intPart + decChar + frac;
    return sample.replace(m[0], body);
  }

  /* ---- free-shipping bars: tagged data-bb-ship-bar="<base-class>" with
     data-threshold (cents) + localized message templates ([[AMT]] slot). ---- */
  function bars() {
    return document.querySelectorAll('[data-bb-ship-bar]');
  }

  /* One-time unlock sheen. Timeout-only removal: two animations share the
     class, so the shorter one's animationend must not cancel the sweep. */
  function sheen(bar) {
    var cls = bar.getAttribute('data-bb-ship-bar') + '--just-met';
    if (bar.classList.contains(cls)) return;
    bar.classList.add(cls);
    setTimeout(function () { bar.classList.remove(cls); }, 700);
  }

  /* Snapshot met-state per bar BEFORE a render... */
  function shipBarsMet() {
    var state = {}, list = bars();
    for (var i = 0; i < list.length; i++) {
      var base = list[i].getAttribute('data-bb-ship-bar');
      state[base] = list[i].classList.contains(base + '--met');
    }
    return state;
  }

  /* ...and AFTER it, sheen bars that crossed INTO met (re-queried: legacy
     render paths recreate the element). */
  function sheenIfCrossed(before) {
    if (!before) return;
    var list = bars();
    for (var i = 0; i < list.length; i++) {
      var base = list[i].getAttribute('data-bb-ship-bar');
      if (before[base] === false && list[i].classList.contains(base + '--met')) sheen(list[i]);
    }
  }

  /* Optimistic bar paint from a client-computed total (cents). Width lands on
     the persistent fill so the CSS width transition runs; the message only
     mutates on real change so role="status" announces once. */
  function updateShipBar(totalCents) {
    if (!isFinite(totalCents)) return;
    var list = bars();
    for (var i = 0; i < list.length; i++) {
      var bar = list[i];
      var base = bar.getAttribute('data-bb-ship-bar');
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10);
      if (!threshold || threshold < 1) continue;
      var wasMet = bar.classList.contains(base + '--met');
      var met = totalCents >= threshold;
      var fill = bar.querySelector('.' + base + '__fill');
      if (fill) fill.style.width = Math.round(Math.min(100, (totalCents * 100) / threshold)) + '%';
      var msg = bar.querySelector('.' + base + '__msg');
      if (msg) {
        var html = met
          ? bar.getAttribute('data-unlocked-html') || ''
          : (bar.getAttribute('data-progress-html') || '').replace(
              '[[AMT]]',
              formatMoneyLike(bar.getAttribute('data-money-fmt') || '$9,999.99', threshold - totalCents)
            );
        if (html && norm(msg.innerHTML) !== norm(html)) msg.innerHTML = html;
      }
      bar.classList.toggle(base + '--met', met);
      if (!wasMet && met) sheen(bar);
    }
  }

  window.BBCartMorph = {
    morph: morph,
    exitRow: exitRow,
    reviveRow: reviveRow,
    formatMoneyLike: formatMoneyLike,
    shipBarsMet: shipBarsMet,
    sheenIfCrossed: sheenIfCrossed,
    updateShipBar: updateShipBar
  };
})();
